import { getTelegramStartParam } from './shared/telegram'
import { createApp, type Component } from 'vue'
import type { AppScreen } from './shared/types'
import { rememberForecastToOpenById } from './shared/forecastQuestionLink'

/** Экраны, которые открываются ссылкой, но живут не во вкладках: «Что впереди». */
type LinkScreen = AppScreen | 'ahead'

const routeScreens: Record<string, LinkScreen> = {
  '': 'today',
  ahead: 'ahead',
  today: 'today',
  forecasts: 'forecast',
  self: 'self',
  couple: 'couple',
  settings: 'settings',
  profile: 'profile',
  portrait: 'portrait',
  'forecast-details': 'forecast-periods',
  'forecast-day': 'forecast-day',
  questions: 'questions',
  energy: 'energy',
  astrology: 'astrology',
  natal: 'natal',
  matrix: 'matrix',
  numerology: 'numerology',
  'numerology-details': 'numerology-details',
  'human-design': 'human-design',
  jyotish: 'jyotish',
  tarot: 'tarot',
  'daily-card': 'daily-card',
  personality: 'personality',
  'full-reading': 'full-reading',
  'year-book': 'year-book',
  resonance: 'resonance',
  // Старая ссылка/startapp на «Мою суть» — теперь ведёт на объединённый экран «Резонанс».
  essence: 'resonance',
  sky: 'sky',
  bazi: 'bazi',
  plans: 'plans',
  notifications: 'notifications',
  referrals: 'referrals',
  streak: 'streak',
  collection: 'collection',
  'couple-dynamic': 'couple-dynamic',
  'my-data': 'my-data',
  books: 'books',
  support: 'support',
}

const routeTitles: Record<LinkScreen, string> = {
  ahead: 'Что впереди — GetKarma',
  today: 'Сегодня — GetKarma',
  forecast: 'Прогнозы — GetKarma',
  self: 'Я — GetKarma',
  couple: 'Мы — GetKarma',
  settings: 'Настройки — GetKarma',
  profile: 'Мои данные — GetKarma',
  portrait: 'Мой портрет — GetKarma',
  'forecast-periods': 'Прогноз — GetKarma',
  'forecast-day': 'Подробный разбор дня — GetKarma',
  questions: 'Вопросы — GetKarma',
  energy: 'Карты рождения — GetKarma',
  astrology: 'Астрология — GetKarma',
  natal: 'Астрология — GetKarma',
  matrix: 'Матрица Судьбы — GetKarma',
  numerology: 'Нумерология — GetKarma',
  'numerology-details': 'Нумерология — GetKarma',
  'human-design': 'Дизайн человека — GetKarma',
  jyotish: 'Джйотиш — GetKarma',
  tarot: 'Таро — GetKarma',
  'daily-card': 'Карта дня — GetKarma',
  personality: 'Мои символы — GetKarma',
  'full-reading': 'Полный разбор личности — GetKarma',
  'year-book': 'Книга твоего года — GetKarma',
  resonance: 'Резонанс — GetKarma',
  sky: 'Небо для тебя — GetKarma',
  bazi: 'Восточная карта (Ба-цзы) — GetKarma',
  plans: 'Премиум и покупки — GetKarma',
  notifications: 'Уведомления — GetKarma',
  referrals: 'Пригласить друга — GetKarma',
  streak: 'Твоя серия — GetKarma',
  collection: 'Коллекция карт — GetKarma',
  'couple-dynamic': 'Динамика пары — GetKarma',
  'my-data': 'Мои данные — GetKarma',
  books: 'Книги — GetKarma',
  support: 'Поддержка — GetKarma',
}

/**
 * Бот пишет «ответ готов» с кнопкой «Открыть» (startapp=ready_<экран>:
 * questions, couple, portrait, forecast, tarot, full-reading, year-book) —
 * открываем сразу нужный экран. Прогноз периода приходит как
 * ready_forecast_<период>_<id> — открываем сам прогноз.
 */
function initialScreenFromStartParam(): LinkScreen | null {
  const param = getTelegramStartParam()
  // Пуш «завтра событие» ведёт прямо в ленту «Что впереди».
  if (param === 'ahead') return 'ahead'
  const forecastMatch = /^ready_forecast_(today|tomorrow|week|month|3months|year)_([A-Za-z0-9-]+)$/.exec(param)
  if (forecastMatch) {
    rememberForecastToOpenById({ forecastId: forecastMatch[2]!, period: forecastMatch[1]! })
    return 'forecast'
  }
  const match = /^ready_(questions|couple|portrait|forecast|tarot|full-reading|year-book)$/.exec(param)
  return match ? (match[1] as AppScreen) : null
}

function initialScreenFromPath(pathname: string): LinkScreen {
  const route = pathname.replace(/\/+$/, '').split('/').filter(Boolean).at(-1) ?? ''
  const screen = routeScreens[route] ?? 'today'
  // Ба-цзы скрыта из интерфейса (план «Целостность», П12): старая прямая
  // ссылка не должна открывать спрятанный раздел.
  return screen === 'bazi' ? 'self' : screen
}

Object.defineProperty(globalThis, 'ctx', {
  configurable: false,
  enumerable: false,
  writable: false,
  value: Object.freeze({}),
})

function normalizedPath(): string {
  const pathname = window.location.pathname.replace(/\/+$/, '')
  return pathname || '/'
}

function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname === '/login'
}

async function rootComponent(pathname: string): Promise<{
  component: Component
  props?: Record<string, unknown>
}> {
  if (isAdminPath(pathname)) {
    document.documentElement.removeAttribute('data-theme')
    document.documentElement.removeAttribute('data-theme-mode')
    // Админка — десктопная: отменяем формулу масштаба от ширины экрана,
    // иначе rem-вёрстка мини-аппа раздула бы её на широких мониторах.
    document.documentElement.style.fontSize = '16px'
    document.body.className = 'admin-body'
    document.title = pathname === '/login'
      ? 'GetKarma — вход в админку'
      : 'GetKarma — Админ'
    await import('@fortawesome/fontawesome-free/css/all.min.css')
    return { component: (await import('./AdminAuthApp.vue')).default }
  }

  const initialScreen = initialScreenFromStartParam() ?? initialScreenFromPath(pathname)
  document.title = routeTitles[initialScreen]
  return {
    component: (await import('./pages/GetKarmaApp.vue')).default,
    props: { initialScreen },
  }
}

/**
 * Время суток на устройстве: фон мини-аппа теплеет к утру и уходит в синеву
 * ночью. Только оформление — на расчёты и тексты не влияет.
 */
function applyDaytime(): void {
  const hour = new Date().getHours()
  const daytime = hour >= 5 && hour < 11
    ? 'morning'
    : hour >= 11 && hour < 17
      ? 'day'
      : hour >= 17 && hour < 22
        ? 'evening'
        : 'night'
  document.documentElement.setAttribute('data-daytime', daytime)
}

async function mountApplication(): Promise<void> {
  const path = normalizedPath()
  if (!isAdminPath(path)) applyDaytime()
  const root = await rootComponent(normalizedPath())
  const app = createApp(root.component, root.props)
  // Значки нарисованы дизайнером и лежат в src/client/icons: компонент виден
  // во всех шаблонах, чтобы не тащить импорт в каждый экран.
  app.component('AppIcon', (await import('./components/AppIcon.vue')).default)
  app.component('IconText', (await import('./components/IconText.vue')).default)
  app.mount('#app')
}

void mountApplication()
